import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useState } from "react";
import { useAuth } from "src/shared/context/useAuth";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { CompanyStatusEnum } from "../enums/sharedEnums/companyStatusEnum";
import { ExpandLess } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { format, parseISO } from "date-fns";
import i18n from "i18next";

export const CompanyInfo = () => {
  const { userInfo } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const companyStatusColor = (companyStatusId: number) => {
    switch (companyStatusId) {
      case CompanyStatusEnum.OTHER:
        return "warning";
      case CompanyStatusEnum.PRE_REGISTRATION:
        return "warning";
      case CompanyStatusEnum.ACTIVE:
        return "success";
      case CompanyStatusEnum.IN_INACTIVITY:
        return "warning";
      case CompanyStatusEnum.BANKRUPTCY:
        return "warning";
      case CompanyStatusEnum.SOLUTION_CLEARANCE:
        return "warning";
      case CompanyStatusEnum.CONCILIATION_PROCEDURE:
        return "warning";
      case CompanyStatusEnum.DELETE:
        return "error";
      case CompanyStatusEnum.DELETION_DUE_TO_MERGER:
        return "error";
      case CompanyStatusEnum.DELETION_DUE_TO_DIVISION:
        return "warning";
      case CompanyStatusEnum.SUSPENSION_OF_ENTRY:
        return "error";
      default:
        return "info";
    }
  };

  const expiryDateColor = (expiryDateString: string) => {
    const expiryDate: Date = parseISO(expiryDateString);
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    if (expiryDate < today) {
      return "error";
    } else if (expiryDate <= thirtyDaysFromNow) {
      return "warning";
    } else {
      return "success";
    }
  };
  return (
    <Box
      sx={{
        backgroundColor: "#E7F0F5",
        borderRadius: 1,
        overflow: "hidden",
        mb: 3,
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          pr: 1.5,
          pl: 1.5,
          pt: 1.5,
          cursor: "pointer",
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.2rem" }}>
          {t("Στοιχεία υπόχρεου")}
        </Typography>
        <IconButton
          size="large"
          sx={{
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          {isExpanded ? <ExpandLess /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 2,
          pb: 2,
          borderTop: "1px solid rgba(255, 255, 255, 0.2)",
          pt: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Chip
            label={t("Υπόχρεος Γ.Ε.ΜΗ.")}
            sx={{
              fontSize: "1rem",
              color: "#FFFFFF",
              backgroundColor: "#6596B5",
              fontWeight: "normal",
              padding: "0.2em 0.5em",
              display: "flex",
              alignItems: "center",
              borderRadius: "1rem",
            }}
          />
          {userInfo?.isSuspended && (
            <Chip
              label={t("Σε αναστολή καταχώρησης")}
              color="warning"
              sx={{
                fontSize: "1rem",
                fontWeight: "normal",
                padding: "0.2em 0.5em",
                borderRadius: "1rem",
              }}
            />
          )}
        </Box>
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={1.5}
        >
          <Typography variant="body1">
            {t("Αρ. ΓΕΜΗ")}:{" "}
            <Link
              target={"_blank"}
              href={`${import.meta.env.VITE_PUBLICITY_URL}/company/${userInfo?.coId}`}
            >
              {userInfo?.coId}
            </Link>
          </Typography>
          <Typography variant="body1">
            {t("ΑΦΜ")}: {userInfo?.coAfm}
          </Typography>
        </Box>
        <Typography variant="body1">
          {t("Επωνυμία")}: {userInfo?.coName}
        </Typography>
      </Box>

      <Collapse in={isExpanded}>
        <Box
          sx={{
            p: 2,
            pt: 1,
            borderTop: "1px solid rgba(255, 255, 255, 0.2)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 1.5,
              width: "100%",
              flexGrow: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{ minWidth: "8rem", fontSize: "1rem" }}
              >
                {t("Κατάσταση")}:
              </Typography>
              <Chip
                sx={{ minWidth: { xs: "8rem", md: "14rem" } }}
                label={
                  i18n.language === "el"
                    ? (userInfo?.companyStatusDescription ?? "")
                    : (userInfo?.companyStatusDescriptionEn ?? "")
                }
                color={companyStatusColor(userInfo?.companyStatusId ?? 0)}
              />
            </Box>
            {userInfo?.isSuspended && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ minWidth: "8rem", fontSize: "1rem" }}
                >
                  {t("Αιτία αναστολής")}:
                </Typography>
                <Box
                  component="ul"
                  sx={{ m: 0, pl: 2.5, fontSize: "1rem" }}
                >
                  {(
                    (i18n.language === "el"
                      ? userInfo?.suspensionReason
                      : userInfo?.suspensionReasonEn) ?? []
                  ).map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </Box>
              </Box>
            )}
            {![
              CompanyStatusEnum.DELETE,
              CompanyStatusEnum.DELETION_DUE_TO_MERGER,
              CompanyStatusEnum.DELETION_DUE_TO_DIVISION,
            ].includes(userInfo?.companyStatusId ?? 0) &&
              ![15, 16].includes(userInfo?.coLegalTypeId ?? 0) && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "1rem", minWidth: "8rem" }}
                  >
                    {t("Ημ/νία λήξης")}:
                  </Typography>
                  <Chip
                    sx={{ minWidth: { xs: "8rem", md: "14rem" } }}
                    label={
                      userInfo?.isAoristi
                        ? t("Αόριστη")
                        : userInfo?.expiryDate
                          ? format(parseISO(userInfo?.expiryDate), "dd/MM/yyyy")
                          : ""
                    }
                    color={
                      userInfo?.isAoristi
                        ? "info"
                        : userInfo?.expiryDate
                          ? expiryDateColor(userInfo?.expiryDate)
                          : "success"
                    }
                  />
                </Box>
              )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 1.5,
              width: "100%",
              flexGrow: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: "1rem" }}>
              {t("Αρμόδια Υπηρεσία ΓΕΜΗ")}:{" "}
              {i18n.language === "el"
                ? (userInfo?.coChamberDescr ?? "")
                : (userInfo?.coChamberDescrEn ?? "")}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "1rem" }}>
              {t("Τηλέφωνο")}: {userInfo?.coChamberPhones}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "1rem" }}>
              {t("email")}: {userInfo?.coChamberEmail}
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
